/**
 * English UI messages: the source catalogue. Every other locale must provide exactly these keys
 * (enforced by the `Messages` type). Placeholders use {name}; plural keys end in `.one` / `.other`
 * and are chosen with Intl.PluralRules. Programme text comes from the API, not from here.
 */
export const en = {
  'shell.skipLink': 'Skip to content',
  'shell.brand': 'Kids World',
  'shell.brandTag': 'Prototype',
  'shell.navLabel': 'Main',
  'shell.menu': 'Menu',
  'shell.closeMenu': 'Close menu',
  'shell.broadcaster': 'Broadcaster',
  'shell.language': 'Language',
  'shell.help': 'Help',
  'shell.disclaimer':
    'Kids World is a provisional concept prototype. All broadcasters, programmes and people shown are fictional. It is not an EBU or member service and implies no endorsement.',
  'shell.helpAndData': 'Help and your data',
  'shell.loading': 'Loading…',

  'nav.discover': 'Town',
  'nav.watch': 'Watch',
  'nav.play': 'Play',
  'nav.create': 'Create',
  'nav.projects': 'My Projects',
  'nav.skills': 'My Skills',

  'error.retry': 'Try again',
  'error.code': 'Error {code}.',
  'error.WEB-NAV-001': "We couldn't find that page. Go back to Discover to keep exploring.",
  'notFound.title': 'Page not found',
  'notFound.back': 'Back to the start',
  'error.generic': "Something on our side didn't work. Try again in a moment.",
  'error.WEB-API-001': "We can't reach Kids World right now. Check your connection and try again.",
  'error.API-DB-001': "Programmes can't be loaded right now. Please try again in a moment.",
  'error.API-RATE-001': 'Too many requests at once. Wait a moment, then try again.',
  'error.CONTENT-CONFIG-001':
    "We couldn't find that broadcaster. Choose another one from the list.",
  'error.CONTENT-CATALOGUE-001':
    "This programme isn't listed for the broadcaster you chose. Pick another programme from the catalogue.",
  'error.WEB-STORAGE-001':
    "This browser can't save anything right now. You can still watch and explore; your choices last until you close the page.",

  'discover.title': 'Discover',
  'discover.intro': 'Watch programmes, play and make things — no sign-in needed.',
  'discover.startExploring': 'Start exploring',
  'discover.featured': 'Featured by {broadcaster}',
  'discover.watch': 'Watch',
  'discover.noItems': 'This broadcaster has no programmes listed right now.',
  'rail.fromBroadcasters': 'From our broadcasters',
  'rail.fromBroadcastersNote': 'Chosen and ordered by the editors of {broadcaster}.',
  'rail.playWithStory': 'Play with a story',
  'rail.playWithStoryNote': 'Programmes that come with a game.',
  'rail.makeSomething': 'Make something',
  'rail.makeSomethingNote': 'Programmes about building, making and trying things out.',
  'rail.empty': 'Nothing here for this broadcaster yet.',
  'rail.previous': 'Show previous programmes',
  'rail.next': 'Show more programmes',
  'rail.seeAll': 'See all programmes',

  'data.title': 'Your data on this device',
  'data.body':
    'You can use Kids World without an account. Your choices (broadcaster, language and, if you allow it, interests) and anything you make are saved only in this browser. Nothing is sent to us.',
  'data.warning':
    'Browsers can delete saved data, for example in private browsing or when site data is cleared. Export projects you want to keep.',
  'data.nothingSaved': 'Nothing is saved on this browser yet.',
  'data.savedChoices': 'Your choices are saved on this browser.',
  'data.projects.one': '{count} saved project',
  'data.projects.other': '{count} saved projects',
  'data.clear': 'Clear my data',
  'data.clearTitle': 'Clear your data on this browser?',
  'data.clearBody':
    'This removes your saved choices, projects and progress from this browser. Export any project you want to keep first. This cannot be undone.',
  'data.cancel': 'Cancel',
  'data.cleared': 'Your data on this browser has been cleared.',

  'card.ages': 'Ages {min}–{max}',
  'card.minutes': 'About {minutes} min',
  'card.episode': 'Season {season}, episode {episode}',
  'card.audio': 'Audio: {languages}',
  'card.captions': 'Captions: {languages}',
  'card.noCaptions': 'No captions yet',
  'card.origin': 'From {origin}',
  'card.game': 'Has a game',
  'card.fictional': 'Fictional demo',
  'card.textLanguage': 'Text in {language}',
  'availability.playable': 'Ready to watch',
  'availability.media-missing': 'Video not available yet',
  'availability.not-streamable': 'Listed only, not playable here',
  'availability.rights-not-started': 'Available from {date}',

  'format.episode': 'Episode',
  'format.short': 'Short',
  'format.special': 'Special',
  'format.clip': 'Clip',
  'topic.science': 'Science',
  'topic.nature': 'Nature',
  'topic.stories': 'Stories',
  'topic.making': 'Making',
  'topic.music': 'Music',
  'topic.sport': 'Sport',
  'topic.history': 'History',
  'topic.animals': 'Animals',
  'warning.mild-peril': 'Mild peril',
  'warning.loud-noises': 'Loud noises',
  'warning.flashing-lights': 'Flashing lights',
  'warning.darkness': 'Dark scenes',
  'credit.production': 'Production',
  'credit.commissioning-member': 'Commissioning member',
  'credit.creator-collaboration': 'Creator collaboration',
  'credit.voice': 'Voice',
  'credit.music': 'Music',

  'watch.title': 'Watch',
  'watch.intro': 'All programmes from {broadcaster}, in the order its editors chose.',
  'filter.legend': 'Find a programme',
  'filter.search': 'Search',
  'filter.searchHint': 'Words from the title or description',
  'filter.topics': 'Interests',
  'filter.age': 'Age',
  'filter.ageAny': 'Any age',
  'filter.format': 'Type',
  'filter.formatAny': 'Any type',
  'filter.clear': 'Clear filters',
  'filter.remember': 'Remember my interests on this browser',
  'filter.rememberHint': 'Optional. Saved only on this device and never sent anywhere.',
  'watch.results.one': '{count} programme',
  'watch.results.other': '{count} programmes',
  'watch.noResults': 'No programmes match these choices.',
  'watch.noResultsHint': 'Try fewer interests, another age or different words.',

  'programme.back': 'All programmes',
  'programme.details': 'Details',
  'programme.made': 'Made by',
  'programme.credits': 'Credits',
  'programme.goodToKnow': 'Good to know',
  'programme.languages': 'Languages',
  'programme.series': 'Episodes in this series',
  'programme.thisEpisode': 'This episode',
  'programme.playable': 'This programme is cleared to play here.',
  'programme.unavailable.media-missing':
    'The video for this programme is not available yet. You can read about it and explore other programmes.',
  'programme.unavailable.not-streamable':
    'This programme can be listed here, but it cannot be played on Kids World.',
  'programme.unavailable.rights-not-started': 'This programme can be watched from {date}.',
  'programme.whatNext': 'What next?',
  'programme.moreProgrammes': 'Explore more programmes',
  'programme.backToDiscover': 'Back to the town',

  'place.cinema': 'Cinema',
  'place.cinemaNote': 'Watch programmes chosen by the editors.',
  'place.lab': 'Discovery Lab',
  'place.labNote': 'Solve circuit challenges from our stories.',
  'place.tower': 'Europe Globe',
  'place.towerNote': 'Choose your broadcaster and language.',
  'place.workshop': 'Makers’ Yard',
  'place.workshopNote': 'Build things in 3D.',
  'place.treehouse': 'Treehouse',
  'place.treehouseNote': 'Your projects and skills, saved on this browser.',
  'hub.title': 'Kids World town',
  'hub.welcomeTitle': 'Welcome to Kids World',
  'hub.welcomeBody':
    'Explore the town. Watch programmes in the Cinema, solve circuit challenges in the Lab, build in Makers’ Yard and choose your broadcaster at the Europe Globe; see your projects and skills in the Treehouse. No sign-in needed.',
  'hub.startExploring': 'Start exploring',
  'hub.placesLabel': 'Places in the town',
  'hub.goIn': 'Go in',
  'hub.lookInside': 'Take a look',
  'hub.building': 'Being built',
  'hub.keysHint': 'Arrow keys choose a place. Enter goes in.',
  'hub.previousPlace': 'Previous place',
  'hub.nextPlace': 'Next place',
  'hub.nowShowing': 'Now showing',
  'hub.loading3d': 'Building the town…',
  'hub.useMap': 'Simple map',
  'hub.use3d': '3D town',
  'hub.mapTitle': 'Town map',
  'error.WORLD-RENDER-001':
    "Your browser can't show the 3D town. The simple map works the same way.",
  'poster.sample': 'Fictional sample',
  'data.close': 'Close',
  'data.open': 'My data',
  'cinema.title': 'Cinema',
  'cinema.back': 'Back to the town',
  'cinema.interests': 'What do you like?',
  'cinema.everything': 'Everything',
  'cinema.allAges': 'All ages',
  'cinema.allTypes': 'All types',
  'cinema.shelfAll': 'All programmes',
  'cinema.shelfGames': 'Stories with a game',
  'cinema.shelfMaking': 'Make and build',
  'cinema.results': 'Programmes for you',
  'tower.title': 'Europe Globe',
  'tower.intro':
    'Each broadcaster chooses programmes for its own country. Your saved things stay with you when you switch.',
  'tower.choose': 'Choose a broadcaster',
  'tower.selected': 'Selected',
  'tower.languages': 'Languages',
  'hub.canvasLabel':
    '3D town. Walk with W A S D or the arrow keys, drag to look around, and press Enter at a door to go in.',
  'hub.pressEnter': 'You are at the door. Press Enter to go in.',
  'hub.walkHint': 'W A S D or arrows to walk · drag to look · scroll to zoom',
  'player.label': 'Video player',
  'player.play': 'Play',
  'player.start': 'Play the programme',
  'player.pause': 'Pause',
  'player.back10': 'Back 10 seconds',
  'player.forward10': 'Forward 10 seconds',
  'player.mute': 'Mute',
  'player.unmute': 'Unmute',
  'player.volume': 'Volume',
  'player.seek': 'Position in the programme',
  'player.time': '{current} of {total}',
  'player.captions': 'Captions',
  'player.captionsOff': 'Off',
  'player.fullscreen': 'Full screen',
  'player.exitFullscreen': 'Exit full screen',
  'player.keys':
    'Keys: Space play or pause · ← → 5 seconds · ↑ ↓ volume · M mute · C captions · F full screen',
  'player.loading': 'Loading the video…',
  'player.watchAgain': 'Watch again',
  'player.captionsFailed': "Captions couldn't load.",
  'player.retry': 'Try again',
  'end.title': 'The end. What next?',
  'end.body': 'You can stop here, or choose something to do next.',
  'programme.nextGame': 'Next: try the circuit challenge',
  'programme.createWorkshop': 'Create in the workshop',
  'programme.playGame': 'Play the game',
  'card.noSpeech': 'No spoken words: music and sounds',
  'error.CONTENT-MEDIA-002':
    "This video couldn't play. Try again, or choose a game or the workshop instead.",
  'shell.fictionalBar':
    'Prototype · all broadcasters, programmes and people are fictional · not an EBU or member service',
  'shell.searchHint': 'Search programmes',
  'nav.cinema': 'Cinema',
  'nav.lab': 'Lab',
  'nav.workshop': 'Workshop',
  'nav.myStuff': 'My stuff',
  'cinema.featured': 'Featured',
  'cinema.featuredGame': 'Featured · with a game',
  'cinema.watchNow': 'Watch now',
  'cinema.tryChallenge': 'Try the challenge',
  'cinema.aboutIt': 'More about it',
  'cinema.rowAll': 'All programmes from {broadcaster}',
  'cinema.rowAllNote': 'In the order the editors chose',
  'cinema.rowGames': 'Watch, then play',
  'cinema.rowGamesNote': 'Stories that come with a challenge in the Lab',
  'cinema.rowMaking': 'Make and build',
  'cinema.rowMakingNote': 'Programmes about building, making and trying things out',
  'cinema.rowLiked': 'Because you like {topics}',
  'cinema.rowLikedNote': 'From the interests you saved on this device',
  'cinema.showAll': 'Show all',
  'search.title': 'Programmes',
  'search.resultsFor': 'Results for “{query}”',
  'search.filters': 'Filters',
  'search.topics': 'Interests',
  'card.thumbFictional': 'Fictional',
  'programme.crumbs': 'Where you are',
  'programme.side': 'Episodes and activities',
  'programme.seasonTitle': 'Season {season}',
  'programme.itemVideo': 'Video · {duration}',
  'programme.watchingNow': 'Watching now',
  'programme.challengeItem': 'Challenge: {title}',
  'programme.challengeMeta': 'Game in the Lab',
  'programme.buildItem': 'Build something in the workshop',
  'programme.buildMeta': 'Workshop',
  'programme.stopAnyTime': 'You can stop at any time. Nothing plays next on its own.',
  'programme.tabOverview': 'Overview',
  'programme.tabLanguages': 'Languages',
  'programme.tabAbout': 'About',
  'programme.learn': "What you'll find out",
  'objective.closedLoop': 'A lamp only lights when the circuit is a closed loop',
  'objective.switchControl': 'A switch opens and closes the loop',
  'objective.faultDiagnosis': 'How to find the gap in a broken circuit',
  'objective.sensorLogic': 'How a light can switch on by itself when it gets dark',
  'game.lab.title': 'Discovery Lab',
  'game.lab.intro':
    'Solve circuit challenges from our stories. Wire up batteries, switches and lamps. Nothing is timed and there is no score.',
  'game.lab.none': 'No challenges are published yet.',
  'game.lab.start': 'Start',
  'game.lab.challenges.one': '{count} challenge',
  'game.lab.challenges.other': '{count} challenges',
  'game.loading': 'Loading the challenge…',
  'game.backToLab': 'Back to the Lab',
  'game.part.battery': 'Battery',
  'game.part.lamp': 'Lamp',
  'game.part.switch': 'Switch',
  'game.part.light-sensor': 'Light sensor',
  'game.end.plus': '+ end',
  'game.end.minus': '− end',
  'game.end.left': 'left end',
  'game.end.right': 'right end',
  'game.terminal': '{part}, {end}',
  'game.find.short-circuit':
    'Short circuit! A wire goes from + to − without a lamp in between, so the battery would get hot. Remove that wire.',
  'game.find.battery-unconnected': 'The battery is not connected to anything yet.',
  'game.find.gap': 'The loop has a gap. Nothing is connected to: {ends}.',
  'game.find.lamp-bypassed':
    'A wire joins both ends of {lamp}, so electricity goes around the lamp instead of through it.',
  'game.find.switch-open': '{switches} is off, so the loop is open.',
  'game.find.sensor-off': 'It is daytime, so the light sensor does not let electricity through.',
  'game.find.hidden-fault':
    'Everything looks connected, but something in the loop does not let electricity through.',
  'game.find.hidden-fault-tester':
    'Everything looks connected, but something in the loop does not let electricity through. Use the tester to find it.',
  'game.find.dim':
    '{lamp} only glows dimly: it shares the battery with another lamp in the same loop.',
  'game.find.not-controlled-switch':
    '{lamp} stays on even when its switch is off: the switch is not in its loop.',
  'game.find.not-controlled-sensor':
    '{lamp} stays on in daylight: the light sensor is not in its loop.',
  'game.find.unknown': 'Not quite yet. Look at the lamps and try again.',
  'game.cond.night': 'at night',
  'game.cond.day': 'by day',
  'game.cond.switchOn': 'with {switch} on',
  'game.cond.switchOff': 'with {switch} off',
  'game.cond.lead': '(Tested {conditions}.)',
  'game.refused.same-terminal': 'Choose a different end.',
  'game.refused.already-connected': 'Those two ends are already connected.',
  'game.refused.no-wires-left': 'No wires left in the box. Remove a wire to use it again.',
  'game.connected': 'Wire connected: {from} to {to}.',
  'game.selected': '{terminal} chosen. Now choose where the wire goes.',
  'game.cancelled': 'Wire cancelled.',
  'game.restarted': 'The board is back to the start.',
  'game.on': 'on',
  'game.off': 'off',
  'game.dim': 'dim',
  'game.lampStatus': '{lamp}: {state}',
  'game.boardHelp':
    'Choose one end, then another end, to connect them with a wire (or drag between them). Esc cancels. Click a switch to flip it.',
  'game.shortWarning': 'Short circuit! The battery is connected straight from + to −.',
  'game.wiresLeft.one': '{count} wire left in the box',
  'game.wiresLeft.other': '{count} wires left in the box',
  'game.daylight': 'Day or night',
  'game.night': 'Night',
  'game.day': 'Day',
  'game.connections': 'Wires and parts',
  'game.connectForm': 'Connect two ends',
  'game.from': 'From',
  'game.to': 'To',
  'game.connect': 'Connect',
  'game.noWires': 'No wires on the board yet.',
  'game.test.ok': 'The tester beeps: this works.',
  'game.test.broken': 'No beep: this is broken!',
  'game.testWire': 'Test wire {wire}',
  'game.testLamp': 'Test {lamp}',
  'game.testPart': 'Test',
  'game.removeWire': 'Remove wire {wire}',
  'game.removed': 'Wire removed: {wire}.',
  'game.remove': 'Remove',
  'game.parts': 'Parts',
  'game.lampReplaced': 'A new lamp is in.',
  'game.replaceLamp': 'New lamp',
  'game.level': 'Level {level}',
  'game.goal': 'Your goal',
  'game.test': 'Test my circuit',
  'game.success': 'You did it!',
  'game.attempts.one': 'Solved after {count} test.',
  'game.attempts.other': 'Solved after {count} tests.',
  'game.notYet': 'Not yet',
  'game.noMoreHints': 'No more hints',
  'game.hint': 'Show a hint',
  'game.startAgain': 'Start again',
  'game.easier': 'Try an easier one',
  'game.harder': 'Try a harder one',
  'game.challengeList': 'Challenges',
  'game.solvedMark': 'Done',
  'error.GAME-CIRCUIT-001': "This challenge couldn't load. Try another challenge.",
  'error.GAME-MISSION-001': 'This challenge is not available. Choose another one in the Lab.',
  'learn.why': 'Why this challenge?',
  'learn.next': 'Next: {title}',
  'learn.reason.start': 'This is where everyone starts.',
  'learn.reason.start-harder': 'You said you have done circuits before, so you start one step up.',
  'learn.reason.step-up':
    'You solved “{challenge}” on your own, so here is a harder one about the same idea.',
  'learn.reason.next-idea': 'Time for a new idea.',
  'learn.reason.next-idea-help':
    '“{challenge}” took some help, so you will come back to that idea later. First, a new idea.',
  'learn.reason.fast-track':
    'You solved two harder challenges on your own in a row, so this new idea starts at the harder level.',
  'learn.reason.step-down':
    '“{challenge}” is not solved yet, so this easier one about the same idea may help.',
  'learn.reason.try-again': '“{challenge}” is not solved yet. Having another go is a good idea.',
  'learn.reason.revisit': 'Back to an idea that needed some help earlier.',
  'learn.reason.all-shown': 'You have shown every idea on your own. Choose any challenge you like.',
  'learn.reason.your-choice': 'You chose this one yourself. That is fine!',
  'learn.obj.closed-loop': 'Closed loops',
  'learn.obj.switch-control': 'Switches',
  'learn.obj.fault-diagnosis': 'Finding faults',
  'learn.obj.sensor-logic': 'Light sensor',
  'learn.status.unobserved': 'Not tried yet',
  'learn.status.emerging': 'Starting',
  'learn.status.practised': 'Practised',
  'learn.status.demonstrated': 'Shown on your own',
  'learn.savePrompt.title': 'Remember my progress on this browser?',
  'learn.savePrompt.body':
    'It is saved only in this browser and never sent to us. You can delete it any time in “My data”.',
  'learn.savePrompt.yes': 'Yes, save it',
  'learn.savePrompt.no': 'No thanks',
  'learn.saving': 'Saving on this browser…',
  'learn.saved': 'Your progress is saved on this browser only.',
  'learn.notSaved': 'Your progress is kept only until you close or reload the page.',
  'learn.start.title': 'Where would you like to start?',
  'learn.start.beginning': 'From the beginning',
  'learn.start.harder': 'I’ve done circuits before',
  'learn.start.note': 'You can always choose a different challenge.',
  'learn.progress.title': 'What you’ve practised',
  'learn.progress.none': 'Nothing tried yet. Your progress will show here.',
  'learn.progress.about':
    'Not tried yet → Starting → Practised → Shown on your own. “Shown on your own” means you solved the harder challenge with at most one hint and three tests.',
  'learn.ev.solved': '“{challenge}”: solved · tests: {tests} · hints: {hints} · {date}',
  'learn.ev.notYet': '“{challenge}”: not solved yet · tests: {tests} · {date}',
  'learn.provisional.one': 'A first guess from {count} challenge, not a mark.',
  'learn.provisional.other': 'A first guess from {count} challenges, not a mark.',
  'learn.continue': 'Continue: {title}',
  'error.LEARN-EVIDENCE-001':
    "We couldn't save your progress on this browser. You can keep playing.",
  'ws.title': 'Makers’ Yard workshop',
  'ws.toolbar': 'Workshop actions',
  'ws.save': 'Save',
  'ws.open': 'Open',
  'ws.undo': 'Undo',
  'ws.redo': 'Redo',
  'ws.export': 'Export',
  'ws.help': 'Help',
  'ws.isSaved': 'Saved on this browser',
  'ws.unsaved': 'Not saved yet',
  'ws.saving': 'Saving…',
  'ws.inventory': 'Parts',
  'ws.addHint':
    'Choose a part to add it to the room. Then drag it, or move it with the arrow keys.',
  'ws.cat.circuit': 'Circuit parts',
  'ws.cat.decor': 'Room things',
  'ws.add': 'Add {object}',
  'ws.added': '{object} added.',
  'ws.removed': '{object} removed.',
  'ws.room': 'Workshop room',
  'ws.view': '3D or plan',
  'ws.view3d': '3D',
  'ws.viewPlan': 'Plan',
  'ws.snap': 'Snap to the grid',
  'ws.loading3d': 'Building the workshop…',
  'ws.zoomIn': 'Zoom in',
  'ws.zoomOut': 'Zoom out',
  'ws.resetCamera': 'Reset view',
  'ws.inspector': 'Properties',
  'ws.objects': 'Things in the room',
  'ws.empty': 'The room is empty. Add parts from the list on the left.',
  'ws.nothingSelected': 'Nothing chosen. Click a thing in the room, or choose it here:',
  'ws.position': 'Position',
  'ws.x': 'Left–right (m)',
  'ws.y': 'Height (m)',
  'ws.z': 'Front–back (m)',
  'ws.rotateLeft': 'Turn left',
  'ws.rotateRight': 'Turn right',
  'ws.scale': 'Size',
  'ws.colour': 'Colour',
  'ws.caption': 'Poster text',
  'ws.switch': 'Switch: {state}',
  'ws.threshold': 'Turns on when the light is below this level',
  'ws.state': '{object}: {state}',
  'ws.wires': 'Wires',
  'ws.connectTo': 'Connect {port} to…',
  'ws.connect': 'Connect',
  'ws.removeWire': 'Remove wire {wire}',
  'ws.wireRemoved': 'Wire removed.',
  'ws.remove': 'Remove',
  'ws.duplicate': 'Copy',
  'ws.deselect': 'Done',
  'ws.portOf': '{object}, {end}',
  'ws.wireStart': 'Wire from {port}. Now choose where it goes.',
  'ws.wireDone': 'Wire connected: {from} to {to}.',
  'ws.wireCancel': 'Wire cancelled.',
  'ws.circuit.no-battery': 'Add a battery to make lamps and fans work.',
  'ws.circuit.several-batteries': 'This workshop uses one battery at a time. Remove a battery.',
  'ws.circuit.short': 'Short circuit! A wire joins + and − without a lamp or fan in between.',
  'ws.circuit.ok': '{on} of {total} lamps and fans are working.',
  'ws.keys':
    'Keys: arrows move · R turn (Shift+R back) · D copy · Delete remove · Ctrl+Z undo · Ctrl+Y redo · Esc stop',
  'ws.saveDialog': 'Save your project',
  'ws.name': 'Name',
  'ws.saveHint': 'It is saved only in this browser. Use Export to keep a copy as a file.',
  'ws.saved': '“{title}” is saved.',
  'ws.cancel': 'Cancel',
  'ws.close': 'Close',
  'ws.openDialog': 'Your projects',
  'ws.newProject': 'Start something new',
  'ws.yourProjects': 'Saved projects',
  'ws.noProjects': 'No saved projects yet.',
  'ws.updated': 'Updated {date}',
  'ws.objectCount.one': '{count} thing',
  'ws.objectCount.other': '{count} things',
  'ws.openProject': 'Open',
  'ws.openNamed': 'Open “{title}”',
  'ws.exportNamed': 'Export “{title}”',
  'ws.deleteNamed': 'Delete “{title}”',
  'ws.deleteProject': 'Delete',
  'ws.opened': '“{title}” is open.',
  'ws.exported': 'Your project file is downloading.',
  'ws.confirmDelete': 'Delete “{title}”?',
  'ws.confirmDeleteBody':
    'This removes it from this browser. Export it first if you want to keep a copy.',
  'ws.continue': 'Continue',
  'ws.discardTitle': 'Start without saving?',
  'ws.unreadable': 'A saved project can’t be opened',
  'ws.downloadRaw': 'Download it',
  'ws.import': 'Open a project file (.json)',
  'ws.leave': 'Leave without saving?',
  'ws.leaveBody': 'Your changes since the last save will be lost.',
  'ws.stay': 'Stay',
  'ws.leaveConfirm': 'Leave',
  'ws.helpTitle': 'How the workshop works',
  'ws.help1':
    'Add parts from the list on the left. Click a thing to choose it, then drag it, turn it or change it on the right.',
  'ws.help2':
    'To wire a circuit, click the round end of one part, then the end of another. A lamp lights only when the loop is closed, just like in the Lab.',
  'ws.help3':
    'Use Plan for a view from above that works well with the keyboard. Save keeps your project in this browser; Export makes a file you can keep.',
  'ws.storageUnavailable':
    'Saving is not possible in this browser (private browsing or blocked storage). You can still build and export.',
  'ws.dismiss': 'OK',
  'ws.posterCaption': 'Lantern room',
  'ws.newTitle.empty': 'My room',
  'ws.newTitle.lighthouse': 'My lantern room',
  'ws.starter.empty': 'Empty room',
  'ws.starter.lighthouse': 'Lighthouse lantern room',
  'ws.obj.floor-tile': 'Floor tile',
  'ws.obj.wall-panel': 'Wall panel',
  'ws.obj.table': 'Table',
  'ws.obj.shelf': 'Shelf',
  'ws.obj.plant': 'Plant',
  'ws.obj.rug': 'Rug',
  'ws.obj.window': 'Window',
  'ws.obj.poster': 'Poster',
  'ws.obj.battery': 'Battery',
  'ws.obj.switch': 'Switch',
  'ws.obj.lamp': 'Lamp',
  'ws.obj.light-sensor': 'Light sensor',
  'ws.obj.fan': 'Fan',
  'ws.port.positive': '+ end',
  'ws.port.negative': '− end',
  'ws.port.a': 'left end',
  'ws.port.b': 'right end',
  'ws.port.in': 'in end',
  'ws.port.out': 'out end',
  'ws.colour.natural': 'Wood',
  'ws.colour.sky': 'Sky blue',
  'ws.colour.leaf': 'Leaf green',
  'ws.colour.sun': 'Sun yellow',
  'ws.colour.berry': 'Berry red',
  'ws.colour.slate': 'Slate grey',
  'ws.refused.too-many-objects': 'The room is full (300 things). Remove something first.',
  'ws.refused.too-many-wires': 'There are too many wires (300). Remove one first.',
  'ws.refused.same-port': 'Choose a different end.',
  'ws.refused.already-connected': 'Those two ends are already connected.',
  'ws.refused.unknown-object': 'That thing is no longer in the room.',
  'ws.refused.not-a-port': 'That is not a wire end.',
  'ws.refused.invalid': 'That change is not possible.',
  'error.WORLD-SAVE-001':
    "We couldn't save this version of your workshop. Your last saved version is unchanged — try exporting your project.",
  'error.WORLD-SAVE-002': "We couldn't save your workshop. Try exporting your project first.",
  'error.WORLD-LOAD-001':
    "We couldn't open this saved project. It has been kept — you can download it or delete it.",
  'error.WORLD-IMPORT-003':
    "This file isn't a Kids World project we can open. Your existing projects are unchanged.",
  'help.title': 'Help',
  'help.keyboardTitle': 'Using a keyboard',
  'help.keyboardBody':
    'Press Tab to move between controls and Enter to choose. The first Tab press offers "Skip to content". Escape closes any window that opens.',
  'help.broadcasterTitle': 'Broadcaster and language',
  'help.broadcasterBody':
    'Choose a broadcaster to see the programmes its editors picked for its country. Each broadcaster offers its own languages. Your saved projects stay with you when you switch.',
  'help.fictionalTitle': 'What "fictional" means',
  'help.fictionalBody':
    'This is a prototype. The broadcasters, programmes and people in it are invented for testing and do not represent real organisations or children.',
  'tree.sections': 'Treehouse sections',
  'tree.whose': 'Whose things are shown',
  'tree.storageUnavailable':
    'This browser can’t save anything (private browsing or blocked storage), so there are no saved projects or skills here.',
  'tree.fictionalTag': 'Fictional sample',
  'tree.profile.sample-new-to-circuits': 'Sample learner: new to circuits',
  'tree.profile.sample-experienced': 'Sample learner: experienced with circuits',
  'tree.profileBody.sample-new-to-circuits':
    'Has tried the first circuit challenge with a lot of help and started one room in the workshop.',
  'tree.profileBody.sample-experienced':
    'Has solved harder closed-loop and switch challenges and built an automatic night light.',
  'tree.sampleNote':
    'You are looking at a fictional sample learner, not a real child. It is a copy on this browser only: what you do now changes only this copy. Your own things are kept.',
  'tree.backToOwn': 'Back to my own',
  'tree.resetSample': 'Start this sample again',
  'tree.removeSample': 'Remove this sample',
  'tree.ownNote': 'These are your own things, saved only on this browser.',
  'tree.trySample': 'Try a sample learner',
  'tree.trySampleBody':
    'See how the Lab and the Treehouse look for a fictional learner. A private copy is made on this browser; nobody else sees it, and your own things stay as they are.',
  'tree.useSample': 'Try it',
  'tree.openSample': 'Open again',
  'tree.useSampleNamed': 'Try {profile}',
  'tree.removeTitle': 'Remove this sample learner?',
  'tree.removeBody':
    'Its copy on this browser, with its projects and progress, will be deleted. You can make a new copy at any time.',
  'tree.loading': 'Loading…',
  'tree.projectsTitle': 'Saved projects',
  'tree.noProjects': 'Nothing saved yet. Build something in Makers’ Yard and save it.',
  'tree.goBuild': 'Go to Makers’ Yard',
  'tree.projectsNote':
    'Projects are saved only on this browser. Clearing browser data removes them — use Export to keep a copy.',
  'tree.skillsTitle': 'Circuit ideas',
  'tree.skillsIntro':
    'What the Lab challenges show so far, idea by idea. Each line says which challenge it comes from.',
  'tree.builtTitle': 'Things built with circuit parts',
  'tree.notAssessed': 'Not assessed',
  'tree.builtNone': 'No saved workshop projects with circuit parts yet.',
  'tree.likesTitle': 'What you like',
  'tree.likesNone': 'No interests saved. You can choose some in the Cinema search.',
  'tree.futureTag': 'Future idea',
  'tree.futureCredential':
    'Certificates are only an idea for the future. Kids World does not award certificates or marks.',
  'tree.reason.no-attempts': 'No challenge for this idea has been tried yet.',
  'tree.reason.tried-not-solved': '“{challenge}” is not solved yet.',
  'tree.reason.solved-with-show-me': '“{challenge}” was solved with “show me”, so this is a start.',
  'tree.reason.solved-entry': '“{challenge}” was solved.',
  'tree.reason.solved-harder-with-help':
    'The harder challenge “{challenge}” was solved with some help.',
  'tree.reason.solved-harder-independently':
    'The harder challenge “{challenge}” was solved on your own.',
  'tree.ev.solved': '“{challenge}”: solved',
  'tree.ev.notYet': '“{challenge}”: not solved yet',
  'tree.ev.harder': 'harder level',
  'tree.ev.entry': 'first level',
  'tree.ev.tests': 'tests: {count}',
  'tree.ev.hints': 'hints: {count}',
  'tree.ev.showMe': 'used “show me”',
  'tree.ev.ownWay': 'on your own',
  'tree.ev.chosen': 'chosen by you',
  'tree.byDay': 'By day: {line}',
  'tree.atNight': 'At night: {line}',
  'tree.sampleProject.firstRoom': 'My first room',
  'tree.sampleProject.nightLight': 'Night light',
  'shell.sampleActive': 'Fictional sample · {profile}',
  'shell.sampleBack': 'Back to my own',
  'offer.title': '{place} is not offered by {broadcaster}',
  'offer.body':
    'Each broadcaster chooses which places it offers. You can choose another broadcaster at the Europe Globe.',
  'offer.kept': 'Anything you saved on this browser is kept.',
  'offer.globe': 'Go to the Europe Globe',
  'offer.town': 'Back to the town',
  'hub.notOffered': 'Not offered here',
  'tree.samplesNotOffered': '{broadcaster} does not offer the sample learners.',
  'kids.readAloud': 'Read it to me',
  'kids.who.title': 'Who is playing?',
  'kids.little': 'I’m little',
  'kids.littleAges': '3–8',
  'kids.bigger': 'I’m bigger',
  'kids.biggerAges': '9+',
  'kids.who.say':
    'Who is playing? Tap the bunny if you are little. Tap the rocket if you are bigger.',
  'kids.change': 'Change who is playing',
  'kids.town.say': 'Where do you want to go? Tap a picture.',
  'time.chipLabel': '{minutes} minutes of screen time left today',
  'time.chip': '{minutes} min left',
  'time.finish': 'Time is up — this one plays to the end',
  'rest.title': 'Time to rest!',
  'rest.body': 'Screen time for today is used up. See you tomorrow!',
  'grown.button': 'For grown-ups',
  'grown.title': 'For grown-ups',
  'grown.hold': 'Grown-ups: press and hold the button for 3 seconds.',
  'grown.holdButton': 'Hold',
  'grown.noLimit': 'No limit',
  'grown.minutes': '{minutes} min',
  'grown.timeTitle': 'Screen time today',
  'grown.timeIntro':
    'Kids World offers only films that fit in the time left and never stops one in the middle. When the time is used up, it says goodbye after the film.',
  'grown.used': 'Used today: {minutes} min',
  'grown.settings': 'Broadcaster, language and data',
  'grown.save': 'Save',
  'grown.close': 'Close',
  'room.hint.little': 'Tap a picture!',
  'room.hint.bigger': 'Choose a film',
  'room.say': 'You are in the cinema! Tap a picture to watch.',
  'room.soonSay': 'This film is not here yet.',
  'room.laterSay': 'This film is too long for today. Next time!',
  'room.back': 'Back to the town',
  'room.films': 'Films',
  'room.watch': 'Watch {title} ({minutes} min)',
  'room.laterLabel': '{title}: too long for today',
  'room.soonLabel': '{title}: not here yet',
  'room.later': 'Next time',
  'room.soon': 'Not here yet',
  'room.minutes': '{minutes} min',
  'room.play': 'Play',
  'room.pause': 'Pause',
  'room.stop': 'Stop the film',
  'room.details': 'About this film',
  'room.all': 'All films',
  'room.loading': 'Opening the cinema…',
} as const;

export type MessageKey = keyof typeof en;
export type Messages = Record<MessageKey, string>;
